import { AlertCircle, Check, Copy, Loader2, PencilLine, RotateCcw, X } from "lucide-react"
import { observer } from "mobx-react"
import { useEffect, useState } from "react"
import { queueModeManager } from "../components/sidebar/queueModeManager"
import { useCodeNavigate } from "../routing"
import { useCodeStore } from "../store/context"
import { copyTextToClipboard } from "../utils/clipboard"
import { getTaskCreationPhaseLabel } from "./taskCreationPhaseLabel"

interface TaskCreationPageProps {
    workspaceId: string
    creationId: string
}

export const TaskCreationPage = observer(({ workspaceId, creationId }: TaskCreationPageProps) => {
    const codeStore = useCodeStore()
    const navigate = useCodeNavigate()
    const creation = codeStore.creation.getCreation(creationId)
    const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle")

    useEffect(() => {
        if (creation?.completedTaskId) {
            const taskId = creation.completedTaskId
            const nextTask = queueModeManager.nextAfterTask(taskId)
            if (nextTask) {
                if (queueModeManager.hasCapacity) {
                    queueModeManager.completeHandoff(nextTask.taskId, taskId)
                    navigate.go("CodeWorkspaceTask", { workspaceId: nextTask.workspaceId, taskId: nextTask.taskId })
                    return
                }
                // Let the created task's route own the visible hold and capacity recovery.
                queueModeManager.trackTask(taskId)
                queueModeManager.waitForCapacity(taskId)
            }
            navigate.go("CodeWorkspaceTask", { workspaceId, taskId })
        }
    }, [creation?.completedTaskId, workspaceId, navigate])

    const handleReturnToComposer = () => {
        navigate.go("CodeWorkspaceTaskCreate", { workspaceId })
    }

    if (!creation) {
        return (
            <div className="h-full flex flex-col items-center justify-center p-8">
                <div className="max-w-md text-center">
                    <div className="text-base-content font-medium">Setup status is no longer available</div>
                    <div className="mt-2 text-sm text-muted">Return to the task composer to recover saved drafts and try again.</div>
                    <button
                        type="button"
                        onClick={handleReturnToComposer}
                        className="btn mt-4 bg-base-200 border border-border px-4 py-2 text-sm font-medium text-base-content hover:bg-base-300 transition-colors cursor-pointer"
                    >
                        Return to task composer
                    </button>
                </div>
            </div>
        )
    }

    const isError = creation.error !== null
    const phase = creation.phase
    const handleRetry = () => {
        codeStore.creation.retryCreation(creationId)
    }

    const handleCancel = async () => {
        await codeStore.creation.cancelCreation(creationId)
        handleReturnToComposer()
    }

    const handleCopyInput = async () => {
        try {
            await copyTextToClipboard(creation.description)
            setCopyState("copied")
        } catch {
            setCopyState("failed")
        }
    }

    return (
        <div className="h-full flex flex-col items-center justify-center p-8">
            <div className="max-w-xl w-full">
                <div className="text-center mb-6">
                    <div className="flex items-center justify-center gap-2">
                        {isError ? <AlertCircle size="1.25rem" className="text-error" /> : <Loader2 size="1.25rem" className="animate-spin text-primary" />}
                        <span className={`text-base font-medium ${isError ? "text-error" : "text-base-content"}`}>
                            {isError ? getTaskCreationPhaseLabel("error") : getTaskCreationPhaseLabel(phase)}
                        </span>
                    </div>
                </div>

                {isError && creation.error && (
                    <div className="mb-6">
                        <div className="mb-2 text-xs text-muted">Error output</div>
                        <div className="bg-error/10 border border-error/20 p-4 max-h-48 overflow-auto">
                            <pre className="text-xs text-error whitespace-pre-wrap font-mono">{creation.error}</pre>
                        </div>

                        <div className="mt-5 flex items-center justify-between gap-3">
                            <div>
                                <div className="text-sm font-medium text-base-content">
                                    {creation.recoveryDraftId ? "Your input is saved" : "Your input is still available"}
                                </div>
                                {creation.recoveryDraftId ? (
                                    <div className="mt-0.5 text-xs text-muted">
                                        It stays in Drafts until setup succeeds
                                        {creation.images.length > 0
                                            ? `, including ${creation.images.length} image${creation.images.length === 1 ? "" : "s"}.`
                                            : "."}
                                    </div>
                                ) : (
                                    <div className="mt-0.5 text-xs text-muted">
                                        Copy it before restarting, or choose Edit input to put it back in the composer.
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => void handleCopyInput()}
                                aria-label="Copy task input"
                                className="btn flex flex-shrink-0 items-center gap-1.5 border border-border bg-base-200 px-2.5 py-1.5 text-xs text-base-content hover:bg-base-300 transition-colors cursor-pointer"
                            >
                                {copyState === "copied" ? <Check size="1em" /> : <Copy size="1em" />}
                                {copyState === "copied" ? "Copied" : "Copy input"}
                            </button>
                        </div>
                        <pre
                            tabIndex={0}
                            className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap border border-border bg-base-100 p-4 font-sans text-sm text-base-content select-text"
                        >
                            {creation.description}
                        </pre>
                        {copyState === "failed" && (
                            <div role="status" className="mt-2 text-xs text-error">
                                Copy failed. Select the input above and copy it manually.
                            </div>
                        )}

                        <div className="flex flex-col gap-3 mt-4 sm:flex-row">
                            <button
                                type="button"
                                onClick={handleRetry}
                                className="btn flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-base-200 border border-border text-base-content text-sm font-medium hover:bg-base-300 transition-colors cursor-pointer"
                            >
                                <RotateCcw size="1em" />
                                Retry
                            </button>
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="btn flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-base-200 border border-border text-base-content text-sm font-medium hover:bg-base-300 transition-colors cursor-pointer"
                            >
                                <PencilLine size="1em" />
                                Edit input
                            </button>
                        </div>
                    </div>
                )}

                {!isError && (
                    <div className="flex flex-col items-center gap-3">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="btn flex items-center justify-center gap-2 px-4 py-2 text-xs text-base-content hover:text-muted transition-colors"
                        >
                            <X size="1em" />
                            Stop
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
})
